import {
  RequirementContextType,
  RequirementDetailsAlertProps,
  RequirementTableItemData
} from "../types";
import { selectorIsRequirementPostApproval } from "../selectors/selectorIsRequirementPostApproval";
import { RequirementStatus } from "@wingspanhq/payments/dist/interfaces";
import { WSButton } from "@wingspanhq/fe-component-library";
import { selectorIsRequirementPendingApproval } from "../selectors/selectorIsRequirementPendingApproval";
import React from "react";
import { selectorIsRequirementArchived } from "../selectors/selectorIsRequirementArchived";

type Props = {
  requirement: RequirementTableItemData;
  contextType: RequirementContextType;
  onOpenHistory: () => void;
};

export const getRequirementOverrideStatusAlert = ({
  requirement,
  contextType,
  onOpenHistory
}: Props): RequirementDetailsAlertProps => {
  const isPostApproval = selectorIsRequirementPostApproval(requirement);

  const isPendingApproval = selectorIsRequirementPendingApproval(requirement);

  if (selectorIsRequirementArchived(requirement)) {
    return {
      theme: "info",
      title: "Requirement archived",
      children:
        "Requirements must be attached to an engagement to become active.",
      icon: "archive"
    };
  }

  if (isPendingApproval) {
    if (contextType === RequirementContextType.Payee) {
      return {
        title: "Action required",
        children:
          "The contractor submitted files for your approval. Review the submission and take action.",
        theme: "warning",
        icon: "alert"
      };
    } else {
      return {
        title: "Awaiting client",
        children:
          "Please wait for your client to review the submission. There is no additional action required at the moment. You will be notified of the outcome and any next steps.",
        theme: "info",
        icon: "info-circle"
      };
    }
  } else if (isPostApproval) {
    if (contextType === RequirementContextType.Payee) {
      if (requirement.status === RequirementStatus.Complete) {
        return {
          title: "Approved by you",
          children:
            "You (or your team) approved the submission and marked the requirement as complete regardless of the validation results.",
          theme: "success",
          icon: "info-circle"
        };
      } else if (requirement.status === RequirementStatus.Incomplete) {
        return {
          title: "Rejected by you",
          children:
            "You (or your team) rejected the submission and marked the requirement as incomplete regardless of the validation results.",
          theme: "error",
          icon: "alert-circle"
        };
      }
    } else {
      if (requirement.status === RequirementStatus.Complete) {
        return {
          title: "Approved by client",
          children:
            "The client approved the submission and marked the requirement as complete regardless of the validation results.",
          theme: "success",
          icon: "info-circle"
        };
      } else if (requirement.status === RequirementStatus.Incomplete) {
        return {
          title: "Rejected by client",
          children: (
            <>
              The client rejected the submission and the requirement is marked
              incomplete.
              <br />
              <WSButton.Link
                p="NONE"
                size="S"
                mt="M"
                onClick={onOpenHistory}
                rightIcon="chevron-right"
              >
                View history
              </WSButton.Link>
            </>
          ),
          theme: "error",
          icon: "info-circle"
        };
      }
    }
  } else if (requirement.status === RequirementStatus.Complete) {
    return {
      theme: "success",
      title: "Requirement complete",
      icon: "check"
    };
  }

  return undefined;
};
